import { computed, ref } from 'vue'
import {
  playerChangelogReleases,
  unseenFreshReleases,
} from '../i18n/changelog'
import { localDayKey } from '../utils/localDay'

const SEEN_KEY = 'dc.changelog.seen'
const DIALOG_KEY = 'dc.changelog.dialog'
const SNOOZE_KEY = 'dc.changelog.snooze'

function readStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {
    /* private mode */
  }
}

const seenId = ref<string | null>(
  typeof localStorage === 'undefined'
    ? null
    : readStorage(localStorage, SEEN_KEY),
)
const dialogShownId = ref<string | null>(
  typeof sessionStorage === 'undefined'
    ? null
    : readStorage(sessionStorage, DIALOG_KEY),
)

function parseSnooze(raw: string | null): { day: string; id: string } | null {
  if (!raw) return null
  const sep = raw.indexOf('|')
  if (sep <= 0) return null
  return { day: raw.slice(0, sep), id: raw.slice(sep + 1) }
}

const snooze = ref<{ day: string; id: string } | null>(
  typeof localStorage === 'undefined'
    ? null
    : parseSnooze(readStorage(localStorage, SNOOZE_KEY)),
)

/**
 * Unread news from the last 3 days, plus the once-per-session dialog flag.
 * Shared across the nav, the home card, and the news page.
 */
export function useChangelogNotice() {
  const unseen = computed(() => unseenFreshReleases(seenId.value))
  const pending = computed(() => unseen.value.length > 0)
  const offerDialog = computed(() => {
    const newest = unseen.value[0]?.id
    if (!newest) return false
    if (dialogShownId.value === newest) return false
    const held = snooze.value
    return !(held && held.day === localDayKey() && held.id === newest)
  })

  /** Hide this note until the next local day. A newer note still appears. */
  function snoozeToday(): void {
    const id = unseen.value[0]?.id
    if (!id) return
    const next = { day: localDayKey(), id }
    snooze.value = next
    writeStorage(localStorage, SNOOZE_KEY, `${next.day}|${next.id}`)
    markDialogShown()
  }

  function markDialogShown(): void {
    const id = unseen.value[0]?.id
    if (!id) return
    dialogShownId.value = id
    writeStorage(sessionStorage, DIALOG_KEY, id)
  }

  /** Opening Novedades counts every current note as read. */
  function markNewsSeen(): void {
    const id = playerChangelogReleases()[0]?.id
    if (!id) return
    seenId.value = id
    writeStorage(localStorage, SEEN_KEY, id)
    dialogShownId.value = id
    writeStorage(sessionStorage, DIALOG_KEY, id)
  }

  return {
    unseen,
    pending,
    offerDialog,
    markDialogShown,
    markNewsSeen,
    snoozeToday,
  }
}
